import React, { useMemo } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// 🎯 Custom Tooltip with booking names + time
const CustomTooltip = ({ active, label, payload, bookings = [], navigate }) => {
  if (!active || !payload || !label) return null;

  // Find the bookings for this day
  const dayLabel = moment(label, "ddd D").format("YYYY-MM-DD");

  const dayBookings = bookings.filter(
    (b) =>
      moment(b.selectedDateTime, "YYYY-MM-DD hh:mm A").format("YYYY-MM-DD") ===
      dayLabel
  );

  return (
    <div className="bg-white dark:bg-neutral-800 border border-border rounded p-3 shadow text-xs text-text w-56 max-h-64 overflow-y-auto">
      <p className="text-yellowlight font-semibold mb-2">{label}</p>
      {dayBookings.length === 0 ? (
        <p className="text-subMain italic">No bookings</p>
      ) : (
        dayBookings.map((b) => {
          const time = moment(b.selectedDateTime, "YYYY-MM-DD hh:mm A").format(
            "hh:mm A"
          );
          return (
            <div
              key={b._id}
              onClick={() => navigate(`/admin/bookings/${b._id}`)}
              className="cursor-pointer mb-1 hover:underline"
            >
              <span className="font-medium">{b.customer_info?.name}</span>{" "}
              <span className="text-subMain">at {time}</span>
            </div>
          );
        })
      )}
    </div>
  );
};

const UpcomingAppointments = ({ bookings = [] }) => {
  const navigate = useNavigate();
  const today = moment().startOf("day");

  const data = useMemo(() => {
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = today.clone().add(i, "days");
      days.push({
        name: day.format("ddd D"), // e.g., Mon 8
        date: day.format("YYYY-MM-DD"),
        Confirmed: 0,
        Cancelled: 0,
      });
    }

    bookings.forEach((booking) => {
      const dayStr = moment(
        booking.selectedDateTime,
        "YYYY-MM-DD hh:mm A"
      ).format("YYYY-MM-DD");
      const status = booking.booking?.status?.toLowerCase();

      const dayData = days.find((d) => d.date === dayStr);
      if (dayData) {
        if (status === "cancelled") dayData.Cancelled += 1;
        else dayData.Confirmed += 1;
      }
    });

    return days;
  }, [bookings]);

  const totalConfirmed = data.reduce((sum, d) => sum + d.Confirmed, 0);
  const totalCancelled = data.reduce((sum, d) => sum + d.Cancelled, 0);

  return (
    <div className="bg-drylight dark:bg-neutral-800 p-5 rounded-lg shadow border border-border">
      <h2 className="text-lg font-semibold text-yellowdark mb-1">
        Upcoming appointments
      </h2>
      <p className="text-xs text-text mb-3">Next 7 days</p>

      <h1 className="text-3xl font-bold text-yellowlight mb-2">
        {totalConfirmed + totalCancelled} booked
      </h1>

      <p className="text-sm text-text">
        Confirmed appointments <strong>{totalConfirmed}</strong>
      </p>
      <p className="text-sm text-text mb-4">
        Cancelled appointments <strong>{totalCancelled}</strong>
      </p>

      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data}>
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
          <Tooltip
            content={<CustomTooltip bookings={bookings} navigate={navigate} />}
          />
          <Bar dataKey="Confirmed" fill="#fed693" />
          <Bar dataKey="Cancelled" fill="#ef4444" />
        </BarChart>
      </ResponsiveContainer>

      <div className="flex justify-start gap-6 mt-3 text-xs text-textGray">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-yellowlight" /> Confirmed
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500" /> Cancelled
        </div>
      </div>
      {/* ✅ New list of upcoming appointments */}
      <div className="mt-6 border-t pt-4 text-sm">
        <p className="text-text font-semibold mb-2">
          Upcoming bookings (next 7 days):
        </p>
        <div className="max-h-40 overflow-y-auto scrollbar-thin space-y-2 pr-2">
          {bookings
            .filter((b) => {
              const bookingDate = moment(
                b.selectedDateTime,
                "YYYY-MM-DD hh:mm A"
              );
              return (
                bookingDate.isSameOrAfter(today) &&
                bookingDate.isBefore(today.clone().add(7, "days"))
              );
            })
            .sort(
              (a, b) =>
                moment(a.selectedDateTime, "YYYY-MM-DD hh:mm A") -
                moment(b.selectedDateTime, "YYYY-MM-DD hh:mm A")
            )
            .map((b, index) => {
              const date = moment(
                b.selectedDateTime,
                "YYYY-MM-DD hh:mm A"
              ).format("ddd, MMM D");
              const time = moment(
                b.selectedDateTime,
                "YYYY-MM-DD hh:mm A"
              ).format("hh:mm A");
              return (
                <div
                  key={b._id}
                  onClick={() => navigate(`/booking/preview/${b._id}`)}
                  className="cursor-pointer hover:underline text-text flex items-center justify-between gap-3"
                >
                  <span className="w-5 text-xs text-subMain">{index + 1}.</span>
                  <div className="flex-1">
                    <span className="font-medium uppercase">
                      {b.customer_info?.name || "Unknown"}
                    </span>{" "}
                  </div>
                  <div className="text-subMain">
                    on {date} - {time}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default UpcomingAppointments;
